import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSparks } from '../../contexts/SparksContext';
import { formatSparks } from '../../api/sparksApi';
import SparksCount from './SparksCount';
import './sparks.css';

const FLIGHT_MS = 1500;

/**
 * Question-page celebration: every award bursts out of Addy's head and flies to a small
 * balance counter in the top-right corner, which ticks up when the bubble lands (§2.6).
 * The top nav is hidden while a question is on screen, so this is the only place the child
 * sees the "+N" during a quiz. Milestone bubbles (🔥) are drawn larger. Three quick answers give
 * three bubbles fanned out horizontally, each landing and ticking in turn.
 */
export default function SparksAddyBurst({ addyRef, showCounter = true }) {
  const { balance, pops, enabled, setBurstMounted, capNotice } = useSparks();
  const counterRef = useRef(null);
  const [landed, setLanded] = useState(() => new Set());
  const [flights, setFlights] = useState({}); // id → { dx, dy, ox }

  useEffect(() => {
    setBurstMounted(true);
    return () => setBurstMounted(false);
  }, [setBurstMounted]);

  // Work out each new bubble's start (Addy's head) and end (the counter) once, when it appears.
  useEffect(() => {
    setFlights((prev) => {
      const next = { ...prev };
      let changed = false;
      pops.forEach((p, i) => {
        if (next[p.id]) return;
        const addy = addyRef?.current?.getBoundingClientRect();
        const counter = counterRef.current?.getBoundingClientRect();
        const startX = addy ? addy.left + addy.width * 0.5 : window.innerWidth - 120;
        const startY = addy ? addy.top + addy.height * 0.12 : window.innerHeight - 200;
        const endX = counter ? counter.left + counter.width * 0.5 : window.innerWidth - 60;
        const endY = counter ? counter.top + counter.height * 0.5 : 24;
        const fan = (i % 3) - 1; // -1, 0, 1 → fan quick bursts out sideways
        next[p.id] = { x: startX + fan * 46, y: startY, dx: endX - (startX + fan * 46), dy: endY - startY, delay: p.delay || 0 };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [pops, addyRef]);

  const inFlight = useMemo(() => pops.filter((p) => !landed.has(p.id)).reduce((s, p) => s + p.amount, 0), [pops, landed]);

  if (!enabled) return null;

  return (
    <>
      <div ref={counterRef} className="sparks-corner-counter" data-testid="sparks-corner-counter" aria-live="polite" style={showCounter ? undefined : { visibility: 'hidden' }}>
        <SparksCount value={Math.max(0, balance - inFlight)} reserve={balance} duration={500} /> <span aria-hidden>✨</span>
      </div>
      {capNotice && <div className="sparks-corner-cap">{capNotice}</div>}
      {pops.map((p) => {
        const f = flights[p.id];
        if (!f) return null;
        const milestone = /🔥/.test(p.label || '');
        return (
          <div
            key={p.id}
            className={`sparks-flight${milestone ? ' sparks-flight--milestone' : ''}`}
            style={{ left: f.x, top: f.y, '--dx': `${f.dx}px`, '--dy': `${f.dy}px`, animationDelay: `${f.delay}ms`, animationDuration: `${FLIGHT_MS}ms` }}
            onAnimationEnd={() => setLanded((prev) => new Set(prev).add(p.id))}
          >
            +{formatSparks(p.amount)} {p.label}
          </div>
        );
      })}
    </>
  );
}
